// Updated MembershipForm.jsx
import { useState } from "react";

const MembershipForm = (props) => {
  const { setShowModal, selectedMembershipType, selectedMembershipFee } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Details
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    whatsapp: "",
    profession: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",

    // Referral Details
    referralName: "",
    referralMobile: "",

    // Agreement
    agreeByLaws: false,
  });

  // Use passed props for membership details
  const membershipType = selectedMembershipType || "Volunteer";
  const membershipFee = selectedMembershipFee || "€0.00";

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  // Handle form submission — saves to SQL Server DB + sends email notification
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const dbPayload = {
      membershipType,
      membershipFee,
      firstName:      formData.firstName,
      lastName:       formData.lastName,
      email:          formData.email,
      phone:          formData.phone,
      whatsapp:       formData.whatsapp,
      profession:     formData.profession,
      addressLine1:   formData.addressLine1,
      addressLine2:   formData.addressLine2,
      city:           formData.city,
      state:          formData.state,
      zipCode:        formData.zipCode,
      country:        formData.country,
      referralName:   formData.referralName,
      referralMobile: formData.referralMobile,
      agreeByLaws:    formData.agreeByLaws,
    };

    const emailPayload = {
      access_key: "5382eb0f-a126-436b-aaa3-4ec251870c25",
      subject: `New Membership Application: ${formData.firstName} ${formData.lastName} (${membershipType})`,
      from_name: "PIEES Membership Portal",
      "Membership Level": membershipType,
      "Membership Fee": membershipFee,
      "First Name": formData.firstName,
      "Last Name": formData.lastName,
      "Email": formData.email,
      "Phone": formData.phone,
      "WhatsApp": formData.whatsapp,
      "Profession": formData.profession,
      "Address": `${formData.addressLine1}${formData.addressLine2 ? ", " + formData.addressLine2 : ""}, ${formData.city}, ${formData.state}, ${formData.zipCode}, ${formData.country}`,
      "Referral Name": formData.referralName || "None",
      "Referral Mobile": formData.referralMobile || "None",
    };

    try {
      // FIXED FOR REMOTE DEPLOYMENT: Use the full VM IP address
      const apiUrl = `http://172.205.208.155:5000/api/membership`;
      
      console.log("🔗 Attempting to connect to:", apiUrl);

      // 1. Save to SQL Server Database
      const dbResponse = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dbPayload),
      });

      if (!dbResponse.ok) {
        const errorData = await dbResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `Server responded with ${dbResponse.status}`);
      }
      
      const dbResult = await dbResponse.json();

      if (dbResult.success) {
        // 2. Data is saved! Now trigger email
        await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            ...emailPayload,
            subject: `[ACTION REQUIRED] New Membership: ${formData.firstName} ${formData.lastName} (${membershipType})`
          }),
        });

        setSubmitStatus('success');
        setTimeout(() => {
          setShowModal(false);
          window.location.href = '/';
        }, 4000);
      } else {
        throw new Error(dbResult.message || "Unknown server error");
      }

    } catch (error) {
      console.error("❌ Submission Error:", error);
      // Detailed alert for debugging
      alert(`⚠️ Connection Error:\n${error.message}\n\nPlease ensure your Node.js server is running and Port 5000 is open in your firewall.`);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center animate-fade-in">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-3xl font-bold text-gray-900">Welcome to PIEES Synergy!</h2>
            <p className="text-gray-600 text-lg">
              Your membership application has been successfully received. 
              Our team will review your details, and a confirmation email has been sent to your inbox.
            </p>
            <p className="text-blue-600 font-semibold pt-4">Redirecting you to the home page...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] overflow-y-auto py-10 px-4 flex items-start justify-center">
      {/* Modal Container */}
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl border border-gray-100 overflow-hidden animate-fade-in my-auto">
          
          {/* Header */}
          <div className="bg-white border-b p-8 flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-black text-gray-900 tracking-tight">Join the Initiative</h2>
              <p className="text-gray-500 mt-1">Complete the form below to become a member of the PIEES synergy.</p>
            </div>
            <button
              onClick={() => setShowModal(false)}
              className="bg-gray-100 text-gray-500 hover:text-gray-900 p-3 rounded-full transition-all hover:bg-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="p-8 md:p-12">
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm flex items-center">
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Oops! We encountered a problem submitting your application. Please try again later or contact support.
              </div>
            )}

            <div className="space-y-10">
              {/* Membership Summary Card */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Selected Plan</span>
                  <h4 className="text-xl font-bold text-slate-900">{membershipType}</h4>
                </div>
                <div className="text-right">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Due</span>
                  <div className="text-2xl font-black text-blue-600">{membershipFee}</div>
                </div>
              </div>

              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="md:col-span-2">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 text-sm">01</span>
                    Personal Information
                  </h3>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="John" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="Doe" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="+1 (555) 000-0000" />
                </div>

                <div className="md:col-span-2 mt-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3 text-sm">02</span>
                    Professional & Location
                  </h3>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Profession</label>
                  <input type="text" name="profession" value={formData.profession} onChange={handleChange} className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="Software Engineer" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Country *</label>
                  <select name="country" value={formData.country} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none">
                    <option value="">Select Country</option>
                    <option value="India">India</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Address *</label>
                  <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="Street name and house number" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="New York" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">State / Province *</label>
                  <input type="text" name="state" value={formData.state} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="NY" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Zip Code *</label>
                  <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} required className="w-full bg-gray-50 border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none" placeholder="10001" />
                </div>
              </div>

              {/* Agreement */}
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <label className="flex items-start cursor-pointer group">
                  <div className="flex items-center h-6 mt-1">
                    <input type="checkbox" name="agreeByLaws" checked={formData.agreeByLaws} onChange={handleChange} required className="w-5 h-5 rounded border-blue-200 text-blue-600 focus:ring-blue-500 transition-all" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-semibold text-slate-800">I agree to the Bylaws Amendments</p>
                    <p className="text-xs text-slate-500 mt-1">By joining, you agree to comply with our organizational rules and mission of economic synergy.</p>
                  </div>
                </label>
              </div>

              {/* Action */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-6 border-t">
                <p className="text-sm text-gray-500">* Required fields for registration</p>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full md:w-auto px-12 py-4 rounded-full font-bold text-white shadow-xl transition-all duration-300 transform ${
                    isSubmitting ? "bg-slate-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 hover:scale-105 active:scale-95"
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : "Complete Registration"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default MembershipForm;
